import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { CompanyOnboardingService } from '../services/company-onboarding.service';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { AuthServiceService } from '@edtech-ui/core/services/authService/auth-service.service';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { CompanyConfirmDialogBoxComponent } from '../recruiter-dashboard/components/company-confirm-dialog-box/company-confirm-dialog-box.component';
import { INDIAN_LOCATIONS } from '../../core/constants/recuritment-constants/indian-locations.constants';

export function phoneValidator(): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		const value = control.value;
		if (value === null || value === undefined || value === '') {
			return null;
		}

		const stringValue = typeof value === 'string' ? value : String(value);
		const trimmedValue = stringValue.trim();

		// Check if it contains only digits and optionally a leading '+'
		const onlyDigitsAndPlus = /^(\+)?\d+$/.test(trimmedValue);
		if (!onlyDigitsAndPlus) {
			return { onlyDigits: true };
		}

		// Check format: optional country code (with +) followed by a 10-digit number starting with 6,7,8,9
		const validFormat = /^(\+\d+)?[6-9]\d{9}$/.test(trimmedValue);
		if (!validFormat) {
			return { invalidPhone: true };
		}

		return null;
	};
}

@Component({
	selector: 'app-company-enrollment',
	standalone: true,
	imports: [CommonModule, ReactiveFormsModule],
	templateUrl: './company-enrollment.component.html',
	styleUrl: './company-enrollment.component.scss'
})
export class CompanyEnrollmentComponent implements OnInit {

	companyForm: FormGroup;
	showReviewModal: boolean = false;
	selectedLogoFile: File | null = null;
	logoPreview: string | null = null;
	companyData: any;
	companyEmail = '';
	officeLocations: string[] = [];
	indianLocations = INDIAN_LOCATIONS;
	filteredLocations: string[] = [];
	showLocationsDropdown = false;
	subscription = new Subscription();

	constructor(
		private companyOnBoardService: CompanyOnboardingService,
		private router: Router,
		private _toastr: ToastrService,
		private dialog: MatDialog,
		private _authService: AuthServiceService
	) {
		this.companyForm = new FormGroup({
			company_name: new FormControl({ value: '', disabled: true }, [
				Validators.required,
				Validators.minLength(2),
				Validators.maxLength(100),
				Validators.pattern('^(?!^[&.\\-\\s]+$)[a-zA-Z0-9\\s&.\\-]+$')
			]),
			industry: new FormControl({ value: '', disabled: true }, [Validators.required]),
			company_url: new FormControl({ value: '', disabled: true }, [
				Validators.required,
				Validators.pattern('^(https?:\\/\\/)?(www\\.)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\\.[a-zA-Z0-9()]{1,6}\\b([-a-zA-Z0-9()@:%_\\+.~#?&//=]*)$')
			]),
			linkedin_profile: new FormControl('', [
				Validators.pattern('^(https?:\\/\\/)?(www\\.)?linkedin\\.com\\/(in|company|school)\\/[a-zA-Z0-9\\-_\.]+\\/?$')
			]),
			about_company: new FormControl('', [
				Validators.required,
				Validators.minLength(10),
				Validators.maxLength(1000),
				Validators.pattern("^[a-zA-Z0-9\\s.',+&\\-\\(\\)\\/]*$")
			]),
			office_locations: new FormControl('', [
				(control: AbstractControl): ValidationErrors | null => {
					if (this.officeLocations && this.officeLocations.length > 0) {
						return null;
					}
					return { required: true };
				}
			]),
			poc_name: new FormControl({ value: '', disabled: true }, [
				Validators.required,
				Validators.minLength(2),
				Validators.maxLength(50),
				Validators.pattern('^[a-zA-Z\\s.]+$')
			]),
			poc_email: new FormControl({ value: '', disabled: true }, [Validators.required, Validators.email]),
			poc_phone: new FormControl({ value: '', disabled: true }, [
				Validators.required,
				phoneValidator()
			]),
			official_email: new FormControl({ value: '', disabled: true }, [Validators.required, Validators.email]),
			hr_name: new FormControl('', [Validators.pattern('^[a-zA-Z\\s.]+$')]),
			hr_email: new FormControl('', [Validators.email]),
			hr_phone: new FormControl('', [phoneValidator()])
		});
	}

	trimPhoneValue(controlName: string): void {
		const control = this.companyForm.get(controlName);
		if (control && typeof control.value === 'string') {
			control.setValue(control.value.trim(), { emitEvent: false });
		}
	}

	onPhoneInput(event: Event, controlName: string): void {
		const input = event.target as HTMLInputElement;
		let value = input.value.replace(/[^0-9+]/g, '');

		const hasPlus = value.startsWith('+');
		value = value.replace(/\+/g, '');
		if (hasPlus) {
			value = '+' + value;
		}

		const maxLength = hasPlus ? 14 : 10;
		if (value.length > maxLength) {
			value = value.slice(0, maxLength);
		}

		const control = this.companyForm.get(controlName);
		if (control) {
			control.setValue(value, { emitEvent: false });
		}
	}


	ngOnInit(): void {
		this.companyOnBoardService.companyRegistration$.subscribe(data => {
			if (data) {
				this.companyData = data;
				this.companyForm.patchValue({
					company_name: data.company_name,
					industry: data.industry,
					company_url: data.company_url,
					poc_name: data.poc_name,
					poc_email: data.poc_email,
					poc_phone: data.poc_phone,
					official_email: data.official_email
				});
			}
		});
	}

	isCustomIndustry(): boolean {
		const industry = this.companyForm.get('industry')?.value;
		const predefinedIndustries = [
			'Information Technology',
			'Education / EdTech',
			'Healthcare & MedTech',
			'FinTech & Banking',
			'Retail & E-commerce',
			'Recruitment & HRTech',
			'Real Estate & PropTech',
			'Logistics & Supply Chain',
			'Travel & Hospitality',
			'Manufacturing',
			'Automotive',
			'Media & Entertainment',
			'Food & Restaurant Industry',
			'Fitness & Wellness',
			'Insurance',
			'LegalTech',
			'SaaS & Software Products'
		];
		return industry && !predefinedIndustries.includes(industry);
	}



	onSubmit() {
		if (this.officeLocations.length === 0) {
			this.companyForm.get('office_locations')?.setErrors({ required: true });
			this.companyForm.get('office_locations')?.markAsTouched();
		}

		if (this.companyForm.invalid) {
			this.companyForm.markAllAsTouched();
			return;
		}

		this.showReviewModal = true;
	}



	closeReviewModal() {
		this.showReviewModal = false;
	}


	onConfirmSubmit() {

		if (this.companyForm.invalid) {
			this.companyForm.markAllAsTouched();
			return;
		}

		const rawValue = this.companyForm.getRawValue();
		const formData = new FormData();
		formData.append('company_name', rawValue.company_name?.trim() || '');
		formData.append('industry', rawValue.industry || '');
		formData.append('company_url', rawValue.company_url?.trim() || '');
		formData.append('linkedin_profile', rawValue.linkedin_profile?.trim() || '');
		formData.append('about_company', rawValue.about_company?.trim() || '');
		formData.append('poc_name', rawValue.poc_name?.trim() || '');
		formData.append('poc_email', rawValue.poc_email?.trim() || '');
		formData.append('poc_phone', rawValue.poc_phone?.trim() || '');
		formData.append('official_email', rawValue.official_email?.trim() || '');
		formData.append('hr_name', rawValue.hr_name?.trim() || '');
		formData.append('hr_email', rawValue.hr_email?.trim() || '');
		formData.append('hr_phone', rawValue.hr_phone?.trim() || '');

		const locations = this.officeLocations;

		locations.forEach((location: string) => {
			formData.append('office_locations', location);
		});

		if (this.selectedLogoFile) {
			formData.append('logo', this.selectedLogoFile);
		}

		this.companyOnBoardService.postCompanyOnBoard(formData).subscribe({
			next: (response) => {

				if (response.Success) {
					if (response.Data?.company_id) {
						localStorage.setItem('companyId', response.Data.company_id);
					}

					const status = response.Data?.status?.toLowerCase() || 'pending';
					localStorage.setItem('companyStatus', status);
					localStorage.setItem('registeredEmail', rawValue.official_email || rawValue.poc_email || '');

					this._toastr.success(response.Message);
					this.companyEmail = response.Data.poc_email;
					this.companyOnBoardService.setCompanyRegistrationData(null);
					this.closeReviewModal();
					this.openCompanyConfirmDialogBox();
				} else {
					this._toastr.error(response.Message);
				}
			},
			error: () => {
				this._toastr.error('Failed to submit profile');
			}
		});
	}


	openCompanyConfirmDialogBox() {
		const dialogRef = this.dialog.open(CompanyConfirmDialogBoxComponent, {
			data: {
				email: this.companyEmail
			},
			disableClose: true
		});

		this.subscription.add(
			dialogRef.afterClosed().subscribe(() => {
				localStorage.clear();
				this.router.navigate(['/recruiter-auth']);
			})
		);
	}

	onLogoSelect(event: any): void {
		const file = event.target.files[0];
		if (!file) return;
		const allowedTypes = [
			'image/png',
			'image/jpeg',
			'image/jpg',
			'image/svg+xml'
		];
		if (!allowedTypes.includes(file.type)) {
			this._toastr.error('Only PNG, JPG and SVG files are allowed');
			return;
		}
		if (file.size > 2 * 1024 * 1024) {
			this._toastr.error('File size should be less than 2MB');
			return;
		}
		this.selectedLogoFile = file;
		const reader = new FileReader();
		reader.onload = () => { this.logoPreview = reader.result as string };
		reader.readAsDataURL(file);
	}


	removeLogo(event: Event): void {
		event.stopPropagation();
		this.selectedLogoFile = null;
		this.logoPreview = null;
	}


	logout() {
		this.subscription.add(
			this._authService.logout(this.router).subscribe({
				next: (response) => {
					if (response.Success) {
						this._toastr.success('Logged out successfully.');
					} else {
						this._toastr.error(response.Message || 'Logout failed');
					}
				},
				error: () => {
					this._toastr.error('An error occurred during logout');
				}
			})
		);
	}


	onLocationInput(event: Event): void {
		const value = (event.target as HTMLInputElement).value;
		if (value && value.trim().length >= 1) {
			const query = value.toLowerCase();
			this.filteredLocations = this.indianLocations.filter(loc =>
				loc.toLowerCase().includes(query)
			);
			this.showLocationsDropdown = true;
		} else {
			this.filteredLocations = [];
			this.showLocationsDropdown = false;
		}
	}

	onLocationFocus(event: Event): void {
		const value = (event.target as HTMLInputElement).value;
		if (value && value.trim().length >= 1) {
			const query = value.toLowerCase();
			this.filteredLocations = this.indianLocations.filter(loc =>
				loc.toLowerCase().includes(query)
			);
			this.showLocationsDropdown = true;
		} else {
			this.filteredLocations = [];
			this.showLocationsDropdown = false;
		}
	}

	onLocationBlur(): void {
		this.showLocationsDropdown = false;
	}

	selectLocation(loc: string): void {
		this.companyForm.get('office_locations')?.setValue(loc);
		this.showLocationsDropdown = false;
		this.filteredLocations = [];
	}

	addLocation(): void {
		const locationControl = this.companyForm.get('office_locations');
		const value = locationControl?.value?.trim();

		if (!value) {
			locationControl?.markAsTouched();
			return;
		}

		// Validation: Check for Indian location match
		const matchedLocation = INDIAN_LOCATIONS.find(loc => loc.toLowerCase() === value.toLowerCase());
		if (!matchedLocation) {
			locationControl?.setErrors({ invalidLocation: true });
			locationControl?.markAsTouched();
			this._toastr.error('Please select a valid location from the suggestions.');
			return;
		}

		// Validation: Check for multiple locations (e.g. comma separated cities)
		const parts = matchedLocation.split(',').map((p: string) => p.trim());
		const partsCount = parts.filter((p: string) => p.length > 0).length;

		const commonRegions = [
			'india', 'usa', 'united states', 'uk', 'united kingdom', 'canada', 'germany', 'australia', 'singapore', 'uae',
			'maharashtra', 'karnataka', 'tamil nadu', 'tamilnadu', 'telangana', 'andhra pradesh', 'delhi', 'gujarat', 'uttar pradesh', 'up', 'haryana', 'punjab', 'kerala', 'rajasthan', 'madhya pradesh', 'bihar', 'west bengal', 'odisha',
			'himachal pradesh', 'chhattisgarh', 'jharkhand', 'chandigarh', 'uttarakhand', 'jammu & kashmir', 'jammu and kashmir', 'goa', 'assam', 'arunachal pradesh', 'manipur', 'meghalaya', 'mizoram', 'nagaland', 'sikkim', 'tripura', 'union territory', 'ut'
		];

		let isMultiple = false;
		if (partsCount > 2) {
			isMultiple = true;
		} else if (partsCount === 2) {
			const secondPart = parts[1].toLowerCase();
			if (!commonRegions.includes(secondPart)) {
				isMultiple = true;
			}
		}

		if (isMultiple) {
			locationControl?.setErrors({ multipleLocations: true });
			locationControl?.markAsTouched();
			this._toastr.error('Please add only one location at a time.');
			return;
		}

		if (!this.officeLocations.includes(matchedLocation)) {
			this.officeLocations.push(matchedLocation);
		}
		locationControl?.setValue('');
		locationControl?.setErrors(null);
	}


	removeLocation(index: number): void {
		this.officeLocations.splice(index, 1);
		const control = this.companyForm.get('office_locations');
		control?.updateValueAndValidity();
		if (this.officeLocations.length === 0) {
			control?.markAsTouched();
		}
	}


}